// Reusable "Handmade — No AI" self-declaration toggle. Any authenticated
// user can flip it via PUT /me/ai-free. Rendered as a warm card with a
// switch; badge preview shows how the profile will read publicly.
import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Hand } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AiFreeToggle({ initialValue = false, onChange }) {
  const [value, setValue] = useState(!!initialValue);
  const [busy, setBusy] = useState(false);

  const flip = async () => {
    const next = !value;
    setBusy(true);
    // Optimistic — undo on failure so the switch stays honest.
    setValue(next);
    try {
      await api.put("/me/ai-free", { ai_free: next });
      onChange && onChange(next);
      toast.success(next
        ? "You're now listed as Handmade — no AI."
        : "AI-free declaration removed.");
    } catch (e) {
      setValue(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="rounded-sm p-5 border"
      style={{
        borderColor: value ? "rgba(111,125,85,0.4)" : "rgba(0,0,0,0.08)",
        background: value ? "rgba(111,125,85,0.06)" : "transparent",
      }}
      data-testid="ai-free-toggle"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="overline inline-flex items-center gap-1.5"
                style={{ color: value ? "var(--moss)" : "var(--ink-soft)" }}>
            <Hand className="h-3 w-3" /> Handmade · No AI
          </div>
          <h3 className="font-serif text-xl mt-1">
            I don&rsquo;t use AI in my work.
          </h3>
          <p className="mt-1 text-sm opacity-75 leading-relaxed">
            When on, we add a small "Handmade · No AI" badge to your public
            profile and listings, and let discovery seekers filter to
            makers like you. Toggle any time.
          </p>
        </div>
        <button
          type="button"
          onClick={flip}
          disabled={busy}
          role="switch"
          aria-checked={value}
          data-testid="ai-free-toggle-switch"
          className="relative inline-flex h-7 w-12 items-center rounded-full transition flex-shrink-0"
          style={{ background: value ? "var(--moss)" : "rgba(0,0,0,0.15)" }}
        >
          <span
            className="inline-block h-5 w-5 rounded-full bg-white shadow transition"
            style={{ transform: `translateX(${value ? 22 : 4}px)` }}
          />
        </button>
      </div>
      {value && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full"
              style={{ background: "var(--moss)", color: "#fff" }}
              data-testid="ai-free-badge-preview">
          <Hand className="h-3 w-3" /> Handmade · No AI
        </div>
      )}
    </div>
  );
}


/** Small standalone badge for embedding on public profile cards, event
 *  cards, VendAgain cards, etc. Renders nothing when ai_free is false so
 *  the caller can inline it unconditionally. */
export function AiFreeBadge({ ai_free, size = "sm", className = "" }) {
  if (!ai_free) return null;
  const isXs = size === "xs";
  return (
    <span
      className={`inline-flex items-center gap-1 font-bold rounded-full ${isXs ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"} ${className}`}
      style={{ background: "var(--moss)", color: "#fff" }}
      data-testid="ai-free-badge"
      title="This maker has self-declared they don't use AI in their work."
    >
      <Hand className={isXs ? "h-2.5 w-2.5" : "h-3 w-3"} />
      No AI
    </span>
  );
}
